import { useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { Link, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { graphql } from '@/gql';
import type { TabAction, TabStatus } from '@/gql/graphql';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { OverviewPanel } from '@/components/ui/OverviewPanel';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatCurrency, formatDateTime, formatShortId } from '@/lib/format';
import { useBackHref } from '@/hooks/useListLocation';
import { openStaffTabReceipt } from '@/lib/staffReceiptDownload';
import { WorkflowActionsCard } from '@/features/sales/WorkflowActionsCard';
import { orderTabActions, TAB_DETAIL_ACTIONS, tabActionLabel, tabActionVariant } from './tabActions';
import { CLEAR_DISCOUNT_UUID, TabLineItemsEditor } from './TabLineItemsEditor';

const TabDoc = graphql(/* GraphQL */ `
  query Tab($id: ID!) {
    tab(id: $id) {
      id
      name
      status
      canEditLineItems
      createdAt
      updatedAt
      recognitionDate
      grandTotal
      recognizedTotal
      amountDiscount
      amountPaid
      amountRefunded
      amountCanceled
      amountDue
      maxAmount
      isInvoice
      exactAccountId
      discount {
        id
        name
      }
      table {
        id
        name
      }
      orders {
        id
        status
        grandTotal
        amountDue
        createdAt
        items {
          id
          productName
          quantity
          price
          lineTotal
        }
      }
      payments {
        id
        status
        paymentMethod
        amount
        amountTip
        createdAt
      }
      refunds {
        id
        status
        paymentMethod
        amount
        createdAt
      }
      allowedActions
    }
  }
`);

const ExactSalesInvoiceForTabDoc = graphql(/* GraphQL */ `
  query ExactSalesInvoiceForTab($tabId: UUID!) {
    exactSalesInvoiceForTab(tabId: $tabId) {
      id
      status
      salesJournalCode
      description
      exactInvoiceId
      invoiceNumber
      error
      recognitionDate
      updatedAt
    }
  }
`);

const ExportExactInvoiceTabDoc = graphql(/* GraphQL */ `
  mutation ExportExactInvoiceTab($tabId: UUID!) {
    exportExactInvoiceTab(tabId: $tabId) {
      id
      status
      invoiceNumber
      error
    }
  }
`);

const PerformTabActionDoc = graphql(/* GraphQL */ `
  mutation PerformTabAction($id: ID!, $action: TabAction!) {
    performTabAction(id: $id, action: $action) {
      id
      status
      allowedActions
    }
  }
`);

const UpdateTabDiscountDoc = graphql(/* GraphQL */ `
  mutation UpdateTabDiscount($input: UpdateTabInput!) {
    updateTab(input: $input) {
      id
      discount {
        id
        name
      }
      amountDiscount
      grandTotal
      amountDue
      canEditLineItems
      orders {
        id
        status
        grandTotal
        amountDue
        createdAt
        items {
          id
          productName
          quantity
          price
          lineTotal
        }
      }
    }
  }
`);

const DiscountsForTabDoc = graphql(/* GraphQL */ `
  query DiscountsForTabEdit {
    discounts(filter: {}, includeInactive: false, page: 1, pageSize: 200) {
      nodes {
        id
        name
        isActive
      }
    }
  }
`);

const STATUS_TONE: Record<TabStatus, BadgeTone> = {
  OPEN: 'info',
  CLOSED: 'neutral',
  FULL_PAID: 'success',
  PARTIAL_PAID: 'warning',
  FULL_REFUNDED: 'warning',
  CANCELED: 'neutral',
  LOSS: 'error',
};

/**
 * Tab detail page. Shows the aggregated tab, then drills into the child
 * orders with their line items, plus the payments and refunds on the tab.
 */
export function TabDetailPage() {
  const backHref = useBackHref('/tabs');
  const { id = '' } = useParams();
  const apollo = useApolloClient();
  const { data, previousData, loading, error, refetch } = useQuery(TabDoc, {
    variables: { id },
  });
  const { data: discountsData } = useQuery(DiscountsForTabDoc, {
    skip: !(data?.tab?.canEditLineItems ?? previousData?.tab?.canEditLineItems),
  });
  const exactInvoiceQuery = useQuery(ExactSalesInvoiceForTabDoc, {
    variables: { tabId: id },
    skip: !id || !(data?.tab?.isInvoice ?? previousData?.tab?.isInvoice),
  });
  const [exportExactInvoice, { loading: exportingExact }] = useMutation(ExportExactInvoiceTabDoc);
  const [performTabAction, { loading: actionLoading }] = useMutation(PerformTabActionDoc);
  const [updateTabDiscount, { loading: discountLoading }] = useMutation(UpdateTabDiscountDoc);
  const [actionError, setActionError] = useState<string | null>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);
  const tab = (data ?? previousData)?.tab;
  const actions = tab
    ? orderTabActions(
        tab.allowedActions.filter((action) =>
          TAB_DETAIL_ACTIONS.includes(action as TabAction),
        ) as TabAction[],
      )
    : [];

  const discountOptions = [
    { value: '', label: '- no discount -' },
    ...(discountsData?.discounts.nodes ?? [])
      .filter((d) => d.isActive)
      .map((d) => ({ value: d.id, label: d.name })),
  ];

  if (loading && !tab) {
    return <div className="p-10 text-center text-sm text-gray-500">Loading…</div>;
  }
  if (error) {
    return <EmptyState title="Could not load tab" description={error.message} />;
  }
  if (!tab) {
    return (
      <EmptyState
        title="Tab not found"
        description="This tab does not exist or has been deleted."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to={backHref} id="admin.tabs.back">
            <Button variant="outline" size="sm" leftIcon={<ArrowLeft className="h-4 w-4" />}>
              Back to tabs
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              {tab.name || `Tab #${formatShortId(tab.id)}`}
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Created {formatDateTime(tab.createdAt)} · Last updated {formatDateTime(tab.updatedAt)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge id="admin.tabs.status" tone={STATUS_TONE[tab.status as TabStatus] ?? 'neutral'}>
            {tab.status.replace('_', ' ').toLowerCase()}
          </Badge>
          {tab.isInvoice && <Badge tone="brand">invoice</Badge>}
        </div>
      </div>

      {tab.isInvoice && (
        <Card title="Exact Online">
          <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <p>
              Exact Account:{' '}
              <span id="admin.tabs.exact.account" className="font-mono text-xs">
                {tab.exactAccountId ? formatShortId(tab.exactAccountId) : '—'}
              </span>
            </p>
            {exactInvoiceQuery.data?.exactSalesInvoiceForTab ? (
              <>
                <p>
                  Export status:{' '}
                  <Badge
                    id="admin.tabs.exact.status"
                    tone={
                      exactInvoiceQuery.data.exactSalesInvoiceForTab.status === 'succeeded'
                        ? 'success'
                        : 'error'
                    }
                  >
                    {exactInvoiceQuery.data.exactSalesInvoiceForTab.status}
                  </Badge>
                  {exactInvoiceQuery.data.exactSalesInvoiceForTab.invoiceNumber != null && (
                    <span id="admin.tabs.exact.invoiceNumber" className="ml-2">
                      Invoice #{exactInvoiceQuery.data.exactSalesInvoiceForTab.invoiceNumber}
                    </span>
                  )}
                </p>
                {exactInvoiceQuery.data.exactSalesInvoiceForTab.error && (
                  <p id="admin.tabs.exact.error" className="text-red-600 dark:text-red-400">
                    {exactInvoiceQuery.data.exactSalesInvoiceForTab.error}
                  </p>
                )}
              </>
            ) : (
              <p className="text-gray-500">Not exported to Exact yet (runs at daily cutoff).</p>
            )}
            {exactInvoiceQuery.data?.exactSalesInvoiceForTab?.status === 'failed' && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                loading={exportingExact}
                onClick={async () => {
                  setActionError(null);
                  try {
                    await exportExactInvoice({ variables: { tabId: tab.id } });
                    await exactInvoiceQuery.refetch();
                  } catch (e) {
                    setActionError(e instanceof Error ? e.message : 'Exact export failed.');
                  }
                }}
              >
                Retry Exact export
              </Button>
            )}
          </div>
        </Card>
      )}

      <WorkflowActionsCard
        actions={actions}
        label={tabActionLabel}
        variant={tabActionVariant}
        actionLoading={actionLoading}
        error={actionError}
        onError={setActionError}
        onReceipt={() => openStaffTabReceipt(apollo, tab.id)}
        onAction={async (action) => {
          await performTabAction({
            variables: { id: tab.id, action },
            refetchQueries: [{ query: TabDoc, variables: { id: tab.id } }],
            awaitRefetchQueries: true,
          });
        }}
      />

      {tab.canEditLineItems && (
        <>
          <Card title="Discount">
            <div className="flex flex-col gap-2 sm:max-w-sm">
              <Select
                aria-label="Tab discount"
                options={discountOptions}
                value={tab.discount?.id ?? ''}
                disabled={discountLoading}
                onChange={async (e) => {
                  const next = e.target.value;
                  setDiscountError(null);
                  try {
                    await updateTabDiscount({
                      variables: {
                        input: {
                          id: tab.id,
                          discountId: next === '' ? CLEAR_DISCOUNT_UUID : next,
                        },
                      },
                    });
                    await refetch();
                  } catch (err) {
                    setDiscountError(
                      err instanceof Error ? err.message : 'Could not update discount.',
                    );
                  }
                }}
              />
              {discountError && (
                <p className="text-sm text-red-600 dark:text-red-400">{discountError}</p>
              )}
            </div>
          </Card>
          <TabLineItemsEditor
            tabId={tab.id}
            status={tab.status}
            amountPaid={tab.amountPaid}
            amountDue={tab.amountDue}
            orders={tab.orders.map((order) => ({
              id: order.id,
              status: order.status,
              createdAt: order.createdAt,
              items: order.items.map((item) => ({
                id: item.id,
                productName: item.productName,
                quantity: item.quantity,
                lineTotal: item.lineTotal,
              })),
            }))}
            onUpdated={() => {
              void refetch();
            }}
          />
        </>
      )}

      <OverviewPanel
        stats={[
          {
            id: 'total',
            label: 'Grand total',
            value: <span id="admin.tabs.total">{formatCurrency(tab.grandTotal)}</span>,
            hint:
              tab.amountDiscount > 0
                ? `${formatCurrency(tab.amountDiscount)} discount`
                : undefined,
          },
          { id: 'paid', label: 'Paid', value: formatCurrency(tab.amountPaid) },
          { id: 'refunded', label: 'Refunded', value: formatCurrency(tab.amountRefunded) },
          { id: 'due', label: 'Due', value: formatCurrency(tab.amountDue) },
        ]}
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Summary" className="lg:col-span-1">
          <dl className="space-y-2 text-sm">
            <Row label="Table">{tab.table?.name ?? '—'}</Row>
            <Row label="Discount">{tab.discount?.name ?? '—'}</Row>
            <Row label="Recognition date">{tab.recognitionDate ?? '—'}</Row>
            <Row label="Max amount">
              {tab.maxAmount !== null && tab.maxAmount !== undefined
                ? formatCurrency(tab.maxAmount)
                : '—'}
            </Row>
            <Row label="Recognized total">{formatCurrency(tab.recognizedTotal)}</Row>
            <Row label="Canceled">{formatCurrency(tab.amountCanceled)}</Row>
          </dl>
        </Card>

        <Card title="Orders on this tab" className="lg:col-span-2">
          {tab.orders.length === 0 ? (
            <EmptyState title="No orders" description="This tab has no orders yet." />
          ) : (
            <div className="space-y-3">
              {tab.orders.map((order) => (
                <div
                  key={order.id}
                  className="rounded-xl border border-gray-200 p-3 dark:border-white/10"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <Link
                        to={`/orders/${order.id}`}
                        className="font-medium text-gray-900 hover:text-brand dark:text-white"
                      >
                        Order #{formatShortId(order.id)}
                      </Link>
                      <span className="ml-2 text-xs text-gray-500">
                        {formatDateTime(order.createdAt)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Badge tone="neutral">{order.status}</Badge>
                      <span>{formatCurrency(order.grandTotal)}</span>
                      {order.amountDue > 0 && (
                        <span className="text-red-600 font-medium">
                          {formatCurrency(order.amountDue)} due
                        </span>
                      )}
                    </div>
                  </div>
                  {!tab.canEditLineItems && (
                    <ul className="mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-300">
                      {order.items.map((item) => (
                        <li key={item.id} className="flex items-center justify-between">
                          <span>
                            {item.quantity}× {item.productName}
                          </span>
                          <span>{formatCurrency(item.lineTotal)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Payments">
          {tab.payments.length === 0 ? (
            <EmptyState title="No payments" description="Nothing paid on this tab yet." />
          ) : (
            <PaymentsRefundsTable
              rows={tab.payments.map((p) => ({
                id: p.id,
                status: p.status,
                method: p.paymentMethod,
                amount: p.amount,
                tip: p.amountTip,
                createdAt: p.createdAt,
              }))}
              showTip
            />
          )}
        </Card>
        <Card title="Refunds">
          {tab.refunds.length === 0 ? (
            <EmptyState title="No refunds" description="Nothing refunded on this tab." />
          ) : (
            <PaymentsRefundsTable
              rows={tab.refunds.map((r) => ({
                id: r.id,
                status: r.status,
                method: r.paymentMethod,
                amount: r.amount,
                createdAt: r.createdAt,
              }))}
            />
          )}
        </Card>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-gray-500">{label}</dt>
      <dd className="font-medium text-gray-900 dark:text-white">{children}</dd>
    </div>
  );
}

interface TxRow {
  id: string;
  status: string;
  method: string;
  amount: number;
  tip?: number;
  createdAt: string;
}

function PaymentsRefundsTable({ rows, showTip }: { rows: TxRow[]; showTip?: boolean }) {
  return (
    <table className="w-full text-left text-sm">
      <thead className="text-xs uppercase tracking-wide text-gray-500">
        <tr>
          <th className="px-2 py-2">Status</th>
          <th className="px-2 py-2">Method</th>
          <th className="px-2 py-2 text-right">Amount</th>
          {showTip && <th className="px-2 py-2 text-right">Tip</th>}
          <th className="px-2 py-2">Created</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-white/10">
        {rows.map((row) => (
          <tr key={row.id}>
            <td className="px-2 py-2">
              <Badge
                tone={
                  row.status === 'success'
                    ? 'success'
                    : row.status === 'failed'
                    ? 'error'
                    : 'neutral'
                }
              >
                {row.status}
              </Badge>
            </td>
            <td className="px-2 py-2">{row.method}</td>
            <td className="px-2 py-2 text-right font-medium">
              {formatCurrency(row.amount)}
            </td>
            {showTip && (
              <td className="px-2 py-2 text-right">
                {row.tip ? formatCurrency(row.tip) : '—'}
              </td>
            )}
            <td className="px-2 py-2">{formatDateTime(row.createdAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
